import { __rest } from "tslib";
import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { Dialog, DialogContent } from "../../components/ui/dialog";
/**
 * @example
 *
 * ```jsx
 * import {
 *   Calculator,
 *   Calendar,
 *   CreditCard,
 *   Settings,
 *   Smile,
 *   User,
 * } from "lucide-react"
 *
 * import {
 *   Command,
 *   CommandEmpty,
 *   CommandGroup,
 *   CommandInput,
 *   CommandItem,
 *   CommandList,
 *   CommandSeparator,
 *   CommandShortcut,
 * } from "@skve/components"
 *
 * function Demo() {
 *   return (
 *     <Command className="rounded-lg border shadow-md md:min-w-[450px]">
 *       <CommandInput placeholder="Type a command or search..." />
 *       <CommandList>
 *         <CommandEmpty>No results found.</CommandEmpty>
 *         <CommandGroup heading="Suggestions">
 *           <CommandItem>
 *             <Calendar />
 *             <span>Calendar</span>
 *           </CommandItem>
 *           <CommandItem>
 *             <Smile />
 *             <span>Search Emoji</span>
 *           </CommandItem>
 *           <CommandItem disabled>
 *             <Calculator />
 *             <span>Calculator</span>
 *           </CommandItem>
 *         </CommandGroup>
 *         <CommandSeparator />
 *         <CommandGroup heading="Settings">
 *           <CommandItem>
 *             <User />
 *             <span>Profile</span>
 *             <CommandShortcut>⌘P</CommandShortcut>
 *           </CommandItem>
 *           <CommandItem>
 *             <CreditCard />
 *             <span>Billing</span>
 *             <CommandShortcut>⌘B</CommandShortcut>
 *           </CommandItem>
 *           <CommandItem>
 *             <Settings />
 *             <span>Settings</span>
 *             <CommandShortcut>⌘S</CommandShortcut>
 *           </CommandItem>
 *         </CommandGroup>
 *       </CommandList>
 *     </Command>
 *   )
 * }
 * ```
 */
const Command = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(CommandPrimitive, Object.assign({ ref: ref, className: cn("flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground", className) }, props)));
});
Command.displayName = CommandPrimitive.displayName;
const CommandDialog = (_a) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    return (React.createElement(Dialog, Object.assign({}, props),
        React.createElement(DialogContent, { className: "overflow-hidden p-0 shadow-lg" },
            React.createElement(Command, { className: "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5" }, children))));
};
const CommandInput = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("div", { className: "flex items-center border-b px-3", "cmdk-input-wrapper": "" },
        React.createElement(Search, { className: "mr-2 h-4 w-4 shrink-0 opacity-50" }),
        React.createElement(CommandPrimitive.Input, Object.assign({ ref: ref, className: cn("flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50", className) }, props))));
});
CommandInput.displayName = CommandPrimitive.Input.displayName;
const CommandList = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(CommandPrimitive.List, Object.assign({ ref: ref, className: cn("max-h-[300px] overflow-y-auto overflow-x-hidden", className) }, props)));
});
CommandList.displayName = CommandPrimitive.List.displayName;
const CommandEmpty = React.forwardRef((props, ref) => (React.createElement(CommandPrimitive.Empty, Object.assign({ ref: ref, className: "py-6 text-center text-sm" }, props))));
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;
const CommandGroup = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(CommandPrimitive.Group, Object.assign({ ref: ref, className: cn("overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground", className) }, props)));
});
CommandGroup.displayName = CommandPrimitive.Group.displayName;
const CommandSeparator = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(CommandPrimitive.Separator, Object.assign({ ref: ref, className: cn("-mx-1 h-px bg-border", className) }, props)));
});
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;
const CommandItem = React.forwardRef((_a, ref) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement(CommandPrimitive.Item, Object.assign({ ref: ref, className: cn("relative flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected='true']:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none", className) }, props)));
});
CommandItem.displayName = CommandPrimitive.Item.displayName;
const CommandShortcut = (_a) => {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (React.createElement("span", Object.assign({ className: cn("ml-auto text-xs tracking-widest text-muted-foreground", className) }, props)));
};
CommandShortcut.displayName = "CommandShortcut";
export { Command, CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandShortcut, CommandSeparator, };
//# sourceMappingURL=command.js.map